import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'surveysides:liststate:';
const DEFAULT_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

interface StorageData {
    timestamp: number;
    state: Record<string, any>;
}

/**
 * Gets the current stored list state object for a given route.
 */
function getStoredData(route: string): StorageData | null {
    const dataStr = sessionStorage.getItem(`${PREFIX}${route}`);
    if (dataStr) {
        try {
            return JSON.parse(dataStr);
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * Clears the list state for a specific route.
 */
export function clearListState(route: string) {
    sessionStorage.removeItem(`${PREFIX}${route}`);
}

/**
 * Clears all list states across the application (e.g., on logout).
 */
export function clearAllListStates() {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(PREFIX)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
}

/**
 * Custom hook to manage and persist list configuration state (pagination, filters, search, etc.)
 * Provides a drop-in replacement for useState with automatic session storage persistence.
 */
export function useListState<T>(
    key: string,
    initialValue: T
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
    const location = useLocation();
    const navType = useNavigationType();
    
    // We only want to evaluate the initial state once on mount.
    const getInitialState = (): T => {
        const route = location.pathname;
        const navState = location.state as { restoreListState?: boolean } | null;
        const shouldRestore = navState?.restoreListState || navType === 'POP';

        if (shouldRestore) {
            const storedData = getStoredData(route);
            if (storedData) {
                // Check TTL
                if (Date.now() - storedData.timestamp > DEFAULT_TTL_MS) {
                    clearListState(route);
                } else if (storedData.state && storedData.state.hasOwnProperty(key)) {
                    return storedData.state[key] as T;
                }
            }
        } else {
            // If we are not restoring (e.g. fresh PUSH from sidebar), we should theoretically clear the state.
            // But since multiple hooks for the same route might call this, we should only clear it once per navigation.
            // To be safe, if we hit a PUSH and no restore flag, we just return the initial value.
            // We can let the subsequent useEffects overwrite the storage.
        }

        return initialValue;
    };

    const [state, setState] = useState<T>(getInitialState);
    const stateRef = useRef(state);
    stateRef.current = state;

    // Reset function
    const reset = useCallback(() => {
        setState(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const route = location.pathname;
        const storedData = getStoredData(route) || { timestamp: Date.now(), state: {} };
        
        storedData.timestamp = Date.now();
        storedData.state[key] = state;
        
        sessionStorage.setItem(`${PREFIX}${route}`, JSON.stringify(storedData));
    }, [state, key, location.pathname]);

    return [state, setState, reset];
}
